import {
  ModalProps,
  createUseModal,
  useFormModal,
} from "@imminently/react/context/modals";
import { Button, Stack } from "@mui/material";

import { Icon } from "@/components/Icon";
import MultilineTextInput from "@/components/MultilineTextInput";
import PatientCardModal from "@/components/PatientCardModal";
import { PatientEncounter } from "@/resources/types";
import { encounterService } from "@/services";
import { useGenericAddPatientModal } from "@/modals/genericForm";

const { useUpdate } = encounterService;

interface PatientArrivedModalProps {
  encounter: PatientEncounter;
}

const PatientArrivedModal = ({ props }: ModalProps<PatientArrivedModalProps>) => {
  const { encounter } = props;

  const { mutateAsync, isLoading } = useUpdate();
  const { methods, notify, close } = useFormModal({
    mode: "all",
    defaultValues: {
      note: "",
    },
  });
  const openEditPatientModal = useGenericAddPatientModal();

  const { control, handleSubmit, formState } = methods;

  const submit = (data: any) => {
    const note = data.note
      ? { text: `Patient arrived with note: ${data.note}` }
      : { text: "Patient arrived" };

    // Set status to seen, submit note
    mutateAsync({
      id: encounter.id,
      payload: { arrived: Date.now(), status: "submitted", note },
    }).catch(() => {
      notify.error("Failed to update patient");
    });

    close(true);
  };

  const submitTriage = (data: any) => {
    const note = data.note
      ? { text: `Patient arrived with note: ${data.note}` }
      : { text: "Patient arrived" };

    // Set status to seen, submit note, then open edit modal
    mutateAsync({
      id: encounter.id,
      payload: { arrived: Date.now(), status: "submitted", note },
    })
      .then((updatedEncounter) => {
        close(true);
        // Open the edit encounter modal after successful update
        // Use updated encounter if available, otherwise fall back to original
        openEditPatientModal({
          mode: "edit",
          encounter: updatedEncounter || encounter,
        });
      })
      .catch(() => {
        notify.error("Failed to update patient");
      });
  };

  return (
    <PatientCardModal
      title="Patient has arrived"
      encounter={encounter}
      onClose={() => close()}
    >
      <MultilineTextInput
        control={control}
        field="note"
        label="Notes..."
        rows={6}
      />

      <Stack direction="row" gap={1} sx={{ ml: "auto" }}>
        <Button
          disabled={isLoading || !formState.isValid}
          color="error"
          startIcon={<Icon icon="check" />}
          onClick={handleSubmit(submitTriage)}
        >
          Triage
        </Button>
        <Button
          disabled={isLoading || !formState.isValid}
          color="success"
          startIcon={<Icon icon="check" />}
          onClick={handleSubmit(submit)}
        >
          Arrived
        </Button>
      </Stack>
    </PatientCardModal>
  );
};

export const usePatientArrivedModal = createUseModal(PatientArrivedModal);
